import { Typography } from "@mui/material";
import * as api from "api/Core";
import { DashboardPatterns } from "assets/jss/common-styles";
import { AppMessageBox } from "components/AppMessageBox";
import { SpinnerIndicator } from "components/CallServer";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { Formaters } from "lib/Formarters";
import { DashboardConsumptionDetailColumn, DashboardConsumptionStorageModel, DashboardConsumptionSummaryColumn, DashboardGraphModel } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import { Pie } from "react-chartjs-2";
import { RenderBarGraph } from "views/Dashboards/components/RenderBarGraph";
import { CommonProps } from "views/Dashboards/ConsumptionAnalysisDashboard";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    tile: DashboardConsumptionSummaryColumn;
}

interface IState {
    data: DashboardGraphModel[];
    loading: boolean;
}

type Props = IProps & CommonProps;

/////////////////////////////////////////////////////////////////////////////////////

function convertUnit(v: number, unit: string) {
    return parseFloat(convertUnit2(v, unit));
}

function convertUnit2(v: number, unit: string) {
    if (unit == "GB")
        return (v / 1024.0 / 1024.0 / 1024.0).toFixed(2);

    if (unit == "MB")
        return (v / 1024.0 / 1024.0).toFixed(2);

    return (v / 1024.0).toFixed(2);
}


export class ConsumptionAnalysisStorageGraph extends React.PureComponent<Props, IState>{
    private unit = "KB";

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            loading: true,
        };
    }

    private getDateFilter() {

        var de = moment().utc().endOf("day");
        var ds = moment().utc().startOf("day");
        ds = ds.add(-12, "months");

        return { startDate: ds.toDate(), endDate: de.toDate() };
    }

    private loadData() {
        api.getDashboardConsumptionAnalysis({
            columnToSummary: this.props.tile,
            detailColumn: DashboardConsumptionDetailColumn.GraphByMonth,
            ...this.getDateFilter(),
        })
            .then((result) => {
                var maxSize = Math.max(...((result.data as DashboardGraphModel[]).map((v) => v.total)));
                if (maxSize >= 1073741824)
                    this.unit = "GB";
                else if (maxSize >= 1048576)
                    this.unit = "MB";

                (result.data as DashboardGraphModel[]).forEach((v) => {
                    v.day = v.day.convertLocalToUtcDate();
                    v.total = convertUnit(v.total, this.unit);
                });

                this.setState({ data: (result.data as DashboardGraphModel[]), loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    public componentDidMount() {
        this.loadData();
    }

    ///
    public render() {
        const { loading, data } = this.state;
        return (
            <>
                <MuiContainer>
                    <MuiContainer margin="dense" >
                        <Typography component="p" variant="h6"><b>{this.props.t("Views.Dashboards.ConsumptionAnalysis.StorageGraph.HeaderTitle", { unit: this.unit }).toString().toLocaleUpperCase()}</b></Typography>
                    </MuiContainer>

                    {
                        loading
                            ? <SpinnerIndicator />
                            : data == null
                                ? <AppMessageBox resource="LoadGraphDataFailed" />
                                : <RenderBarGraph color="orange" data={this.state.data} lastItemFade={0.6} />
                    }
                </MuiContainer>

                {
                    !loading &&
                    <DistributionPieGraph {...this.props} unit={this.unit} />
                }
            </>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IDistributionState {
    data: DashboardConsumptionStorageModel;
    loading: boolean;
}

interface IDistributionProps extends CommonProps {
    unit: string;
}

class DistributionPieGraph extends React.PureComponent<IDistributionProps, IDistributionState>
{
    constructor(props: IDistributionProps) {
        super(props);

        this.state = {
            data: null,
            loading: true,
        };
    }

    ///
    private getDateFilter() {
        return {
            startDate: moment().utc().startOf("month").toDate()
        };
    }

    ///
    private loadData() {

        api.getDashboardConsumptionAnalysis({
            columnToSummary: DashboardConsumptionSummaryColumn.Storage,
            detailColumn: DashboardConsumptionDetailColumn.StorageDistribution,
            ...this.getDateFilter(),
        })
            .then((result) => {
                this.setState({ data: result.data, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public renderGraph() {

        const { data } = this.state;

        var ds: number[] = [
            convertUnit(data.nonInstitutionalSize, this.props.unit),
            convertUnit(data.institutionalSize, this.props.unit),
            convertUnit(data.courseSize, this.props.unit),
            convertUnit(data.kwnlTestSize, this.props.unit),
            convertUnit(data.thumbnailsSize, this.props.unit)
        ];
        var labels: string[] = [
            this.props.t("Views.Dashboards.ConsumptionAnalysis.DistributionPieGraph.DS2label"),
            this.props.t("Views.Dashboards.ConsumptionAnalysis.DistributionPieGraph.DS1Label"),
            this.props.t("Views.Dashboards.ConsumptionAnalysis.DistributionPieGraph.DS3Label"),
            this.props.t("Views.Dashboards.ConsumptionAnalysis.DistributionPieGraph.DS4Label"),
            this.props.t("Views.Dashboards.ConsumptionAnalysis.DistributionPieGraph.DS5Label")

        ];
        var backgroundColors: string[] = [
            DashboardPatterns.pie.green.backgroundColor,
            DashboardPatterns.pie.yellow.backgroundColor,
            DashboardPatterns.pie.orange.backgroundColor,
            DashboardPatterns.pie.purple.backgroundColor,
            DashboardPatterns.pie.blue.backgroundColor
        ];

        return (
            <Pie
                data={
                    {
                        labels: labels,
                        datasets: [
                            {
                                label: null,
                                backgroundColor: backgroundColors,
                                data: ds
                            }
                        ]
                    }
                }
                options={
                    {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                display: true,
                                position: 'bottom',
                            },

                            tooltip: {
                                callbacks: {
                                    label: (item: any) => {
                                        return `${item.chart.data.labels[item.dataIndex]}: ${Formaters.localeNumber(item.chart.data.datasets[item.datasetIndex].data[item.dataIndex])} ${this.props.unit}`;
                                    },
                                }
                            }
                        }
                    }
                }

            />
        );
    }

    ///
    public render() {

        const { loading, data } = this.state;

        if (loading)
            return <SpinnerIndicator />;

        if (data == null)
            return <AppMessageBox resource="LoadGraphDataFailed" />;

        return (
            <MuiContainer>
                <MuiContainer>
                    <Typography component="p" variant="button">{this.props.t("Views.Dashboards.ConsumptionAnalysis.DistributionPieGraph.InfoTitle")}</Typography>
                    <MuiMuted>
                        <small>
                            {this.props.t("Views.Dashboards.ConsumptionAnalysis.DistributionPieGraph.InfoSubtitle")}
                        </small>
                    </MuiMuted>
                </MuiContainer>
                <MuiContainer>
                    <Typography component="p" variant="button">{this.props.t("Views.Dashboards.ConsumptionAnalysis.DistributionPieGraph.HeaderTitle")}</Typography>
                </MuiContainer>
                <div style={{ position: "relative", height: "40vh" }}>
                    {this.renderGraph()}
                </div>
            </MuiContainer>
        );
    }
}